/**
 * Admin Island Settings
 * Manage marketplace islands - add, edit, activate/deactivate
 * Designed for easy addition of new islands (4th, 5th island, etc.)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  MapPin, Plus, Save, Trash2, Check, X, Globe, Eye, EyeOff,
  Loader2, AlertCircle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdmin } from './AdminContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

// Default island flags/emojis
const ISLAND_FLAGS = {
  curacao: '🇨🇼',
  aruba: '🇦🇼',
  bonaire: '🇧🇶',
  'sint-maarten': '🇸🇽',
  saba: '🇧🇶',
  'st-eustatius': '🇧🇶',
  default: '🏝️'
};

const AdminIslandSettings = () => {
  const { apiCall, formatDate } = useAdmin();
  
  const [islands, setIslands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // New island form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newIsland, setNewIsland] = useState({
    code: '',
    name: '',
    flag: '🏝️',
    active: true,
    currency: 'USD',
    timezone: 'America/Curacao'
  });
  
  // Edit island
  const [editingIsland, setEditingIsland] = useState(null);
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Fetch islands
  const fetchIslands = useCallback(async () => {
    try {
      setLoading(true);
      // The /api/islands endpoint returns an array directly
      const response = await apiCall('get', '/api/islands?active_only=false');
      console.log('Islands API response:', response);
      // Handle both array response and {islands: []} response
      const islandsArray = Array.isArray(response) ? response : (response?.islands || []);
      setIslands(islandsArray);
    } catch (error) {
      console.error('Failed to fetch islands:', error);
      // Don't show toast here since apiCall already handles it
      setIslands([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);
  
  useEffect(() => {
    fetchIslands();
  }, [fetchIslands]);
  
  // Generate code from name
  const generateCode = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };
  
  // Handle new island name change
  const handleNameChange = (name) => {
    setNewIsland(prev => ({
      ...prev,
      name,
      code: generateCode(name)
    }));
  };
  
  // Add new island
  const handleAddIsland = async () => {
    if (!newIsland.code || !newIsland.name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Check for duplicate code
    if (islands.some(i => i.code === newIsland.code)) {
      toast.error('An island with this code already exists');
      return;
    }
    
    try {
      setSaving(true);
      await apiCall('post', '/api/islands', newIsland);
      toast.success(`${newIsland.name} added successfully!`);
      setShowAddForm(false);
      setNewIsland({
        code: '',
        name: '',
        flag: '🏝️',
        active: true,
        currency: 'USD',
        timezone: 'America/Curacao'
      });
      fetchIslands();
    } catch (error) {
      console.error('Failed to add island:', error);
    } finally {
      setSaving(false);
    }
  };
  
  // Update island
  const handleUpdateIsland = async (island) => {
    try {
      setSaving(true);
      await apiCall('put', `/api/islands/${island.code}`, island);
      toast.success(`${island.name} updated successfully!`);
      setEditingIsland(null);
      fetchIslands();
    } catch (error) {
      console.error('Failed to update island:', error);
    } finally {
      setSaving(false);
    }
  };
  
  // Toggle island active status
  const handleToggleActive = async (island) => {
    const updated = { ...island, active: !island.active };
    try {
      await apiCall('put', `/api/islands/${island.code}`, updated);
      toast.success(`${island.name} is now ${updated.active ? 'active' : 'inactive'}`);
      fetchIslands();
    } catch (error) {
      console.error('Failed to toggle island:', error);
    }
  };
  
  // Delete island
  const handleDeleteIsland = async (code) => {
    try {
      setSaving(true);
      await apiCall('delete', `/api/islands/${code}`);
      toast.success('Island deleted successfully');
      setDeleteConfirm(null);
      fetchIslands();
    } catch (error) {
      console.error('Failed to delete island:', error);
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Globe className="w-6 h-6 text-primary" />
            Island Management
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Add, edit, or manage marketplace islands. Active islands appear in the navigation.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchIslands}
            className="p-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Island
          </button>
        </div>
      </div>
      
      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-2xl font-bold text-primary">{islands.length}</div>
          <div className="text-sm text-muted-foreground">Total Islands</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-2xl font-bold text-green-500">{islands.filter(i => i.active).length}</div>
          <div className="text-sm text-muted-foreground">Active</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-2xl font-bold text-gray-500">{islands.filter(i => !i.active).length}</div>
          <div className="text-sm text-muted-foreground">Inactive</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4 flex items-center justify-center">
          <span className="text-3xl">
            {islands.map(i => ISLAND_FLAGS[i.code] || '🏝️').join(' ')}
          </span>
        </div>
      </div>
      
      {/* Islands List */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border bg-secondary/30">
          <h3 className="font-semibold">Configured Islands</h3>
        </div>
        
        <div className="divide-y divide-border">
          {islands.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No islands configured yet.</p>
              <p className="text-sm">Click "Add Island" to get started.</p>
            </div>
          ) : (
            islands.map(island => (
              <div 
                key={island.code}
                className={`p-4 flex items-center justify-between hover:bg-secondary/20 transition-colors ${
                  !island.active ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-center gap-4">
                  <span className="text-3xl">{ISLAND_FLAGS[island.code] || island.flag || '🏝️'}</span>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{island.name}</span>
                      {island.active ? (
                        <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-500 rounded-full">
                          Active
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 bg-gray-500/20 text-gray-500 rounded-full">
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Code: <code className="bg-secondary px-1 rounded">{island.code}</code>
                      {island.currency && <span className="ml-2">Currency: {island.currency}</span>}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleActive(island)}
                    className={`p-2 rounded-lg transition-colors ${
                      island.active 
                        ? 'bg-green-500/20 text-green-500 hover:bg-green-500/30' 
                        : 'bg-gray-500/20 text-gray-500 hover:bg-gray-500/30'
                    }`}
                    title={island.active ? 'Deactivate' : 'Activate'}
                  >
                    {island.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => setEditingIsland(island)}
                    className="p-2 rounded-lg bg-blue-500/20 text-blue-500 hover:bg-blue-500/30 transition-colors"
                    title="Edit"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(island)}
                    className="p-2 rounded-lg bg-red-500/20 text-red-500 hover:bg-red-500/30 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* How to Add Islands Info */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-500">Adding a New Island (e.g., Sint Maarten)</h4>
            <ol className="text-sm text-muted-foreground mt-2 space-y-1 list-decimal list-inside">
              <li>Click "Add Island" and enter the island name</li>
              <li>The system will auto-generate a URL-friendly code</li>
              <li>Set the island to "Active" to make it visible in navigation</li>
              <li>Listings from users in that location will auto-match</li>
              <li>No code changes required - fully database-driven!</li>
            </ol>
          </div>
        </div>
      </div>
      
      {/* Add Island Dialog */}
      <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              Add New Island
            </DialogTitle>
            <DialogDescription>
              Add a new marketplace island. It will be immediately available once activated.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Island Name *</label>
              <input
                type="text"
                value={newIsland.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="e.g., Sint Maarten"
                className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-background"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Code (auto-generated)</label>
              <input
                type="text"
                value={newIsland.code}
                onChange={(e) => setNewIsland(prev => ({ ...prev, code: e.target.value }))}
                placeholder="sint-maarten"
                className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-secondary/50"
              />
              <p className="text-xs text-muted-foreground mt-1">Used in URLs and database. Keep lowercase with hyphens.</p>
            </div>
            
            <div>
              <label className="text-sm font-medium">Flag/Emoji</label>
              <input
                type="text"
                value={newIsland.flag}
                onChange={(e) => setNewIsland(prev => ({ ...prev, flag: e.target.value }))}
                placeholder="🏝️"
                className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-background text-2xl text-center"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Currency</label>
                <select
                  value={newIsland.currency}
                  onChange={(e) => setNewIsland(prev => ({ ...prev, currency: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-background"
                >
                  <option value="USD">USD ($)</option>
                  <option value="XCG">XCG (ƒ)</option>
                  <option value="AWG">AWG (ƒ)</option>
                  <option value="EUR">EUR (€)</option>
                </select>
              </div>
              
              <div>
                <label className="text-sm font-medium">Timezone</label>
                <select
                  value={newIsland.timezone}
                  onChange={(e) => setNewIsland(prev => ({ ...prev, timezone: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-background"
                >
                  <option value="America/Curacao">Atlantic (AST)</option>
                  <option value="America/New_York">Eastern (EST)</option>
                  <option value="Europe/Amsterdam">Europe (CET)</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="newIslandActive"
                checked={newIsland.active}
                onChange={(e) => setNewIsland(prev => ({ ...prev, active: e.target.checked }))}
                className="rounded"
              />
              <label htmlFor="newIslandActive" className="text-sm">
                Activate immediately (visible in navigation)
              </label>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleAddIsland}
              disabled={saving || !newIsland.name || !newIsland.code}
              className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Add Island
            </button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit Island Dialog */}
      <Dialog open={!!editingIsland} onOpenChange={() => setEditingIsland(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="w-5 h-5 text-primary" />
              Edit Island
            </DialogTitle>
          </DialogHeader>
          
          {editingIsland && (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium">Island Name</label>
                <input
                  type="text"
                  value={editingIsland.name}
                  onChange={(e) => setEditingIsland(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-background"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Code (read-only)</label>
                <input
                  type="text"
                  value={editingIsland.code}
                  disabled
                  className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-secondary/50 text-muted-foreground"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Flag/Emoji</label>
                <input
                  type="text"
                  value={editingIsland.flag || ''}
                  onChange={(e) => setEditingIsland(prev => ({ ...prev, flag: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-background text-2xl text-center"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="editIslandActive"
                  checked={editingIsland.active}
                  onChange={(e) => setEditingIsland(prev => ({ ...prev, active: e.target.checked }))}
                  className="rounded"
                />
                <label htmlFor="editIslandActive" className="text-sm">
                  Active (visible in navigation)
                </label>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setEditingIsland(null)}
              className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              onClick={() => handleUpdateIsland(editingIsland)}
              disabled={saving}
              className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Changes
            </button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <Trash2 className="w-5 h-5" />
              Delete Island
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirm?.name}"? This action cannot be undone.
              Listings from this island will no longer be filtered correctly.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => setDeleteConfirm(null)}
              className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              onClick={() => handleDeleteIsland(deleteConfirm.code)}
              disabled={saving}
              className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Delete Island
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminIslandSettings;
